import { Controller } from "@hotwired/stimulus"
import { countries } from "./dialcode_data"
import { countryCodeEmoji } from "country-code-emoji"
import TomSelect from "tom-select"
import { createPopper } from "@popperjs/core"

const HEADER_BUFFER = 100

export default class extends Controller {
  static targets = ['select', 'phoneNumber']

  connect() {
    const { countryCode, localNumber, country } = this.selectTarget.dataset

    // Build options from countries data
    const options = countries.map(c => ({
      value: c.iso2,
      text: `${countryCodeEmoji(c.iso2)} ${c.name.replace(/\s*\(.*\)$/, '')} (+${c.dialCode})`,
      dialCode: c.dialCode
    }))

    this.tomselect = new TomSelect(this.selectTarget, {
      options: options,
      items: [country.toLowerCase()],
      plugins: {
        dropdown_input: {}
      },
      dropdownParent: 'body',
      render: {
        item: (data, escape) => {
          return `<div>${countryCodeEmoji(data.value)}</div>`
        },
        option: (data, escape) => {
          return `<div>${escape(data.text)}</div>`
        }
      },
      onChange: (value) => {
        const selected = options.find(o => o.value === value)
        if (selected) {
          this.phoneNumberTarget.dataset.countryCode = selected.dialCode
        }
      }
    })

    this.setupPopper()
    this.setupEventHandlers()

    this.phoneNumberTarget.dataset.countryCode = countryCode
    this.updatePhoneNumberInput(this.phoneNumberTarget.querySelector('input'), localNumber)
  }

  setupPopper() {
    this.popper = createPopper(this.tomselect.control, this.tomselect.dropdown, {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'offset',
          options: { offset: [0, -1] },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top-start'],
            padding: HEADER_BUFFER,
            rootBoundary: 'viewport',
          },
        },
        {
          name: 'preventOverflow',
          options: { boundary: 'viewport' },
        },
      ],
    })

    this.tomselect.dropdown.style.minWidth = '200px'
    this.tomselect.dropdown.style.zIndex = '100'
  }

  applyDropdownStyling() {
    if (!this.tomselect.dropdown) return

    const placement = this.tomselect.dropdown.getAttribute('data-popper-placement') || 'bottom-start'
    const isTop = placement.startsWith('top')

    if (isTop) {
      this.tomselect.wrapper.classList.add('dropdown-up')
      this.tomselect.dropdown.classList.add('dropdown-up')
      this.tomselect.dropdown.style.setProperty('border-radius', '0.75rem 0.75rem 0 0', 'important')
      this.tomselect.dropdown.style.setProperty('border-bottom', 'none', 'important')
      this.tomselect.dropdown.style.setProperty('border-top', '1px solid #E3E3E3', 'important')
    } else {
      this.tomselect.wrapper.classList.remove('dropdown-up')
      this.tomselect.dropdown.classList.remove('dropdown-up')
      this.tomselect.dropdown.style.setProperty('border-radius', '0 0 0.75rem 0.75rem', 'important')
      this.tomselect.dropdown.style.setProperty('border-top', 'none', 'important')
      this.tomselect.dropdown.style.setProperty('border-bottom', '1px solid #E3E3E3', 'important')
    }
  }

  setupEventHandlers() {
    this.scrollHandler = () => {
      if (this.tomselect.isOpen) {
        const rect = this.selectTarget.getBoundingClientRect()
        if (rect.top < HEADER_BUFFER) {
          this.tomselect.close()
          return
        }
        if (this.popper) {
          this.popper.update().then(() => {
            this.applyDropdownStyling()
          })
        }
      }
    }

    this.tomselect.on('dropdown_open', () => {
      if (this.popper) {
        this.popper.update().then(() => {
          this.applyDropdownStyling()
        })
      }

      this.scrollableAncestors = []
      let el = this.selectTarget.parentElement
      while (el) {
        const style = getComputedStyle(el)
        if (style.overflow === 'auto' || style.overflow === 'scroll' ||
            style.overflowY === 'auto' || style.overflowY === 'scroll') {
          el.addEventListener('scroll', this.scrollHandler)
          this.scrollableAncestors.push(el)
        }
        el = el.parentElement
      }
      window.addEventListener('scroll', this.scrollHandler)
    })

    this.tomselect.on('dropdown_close', () => {
      if (this.scrollableAncestors) {
        this.scrollableAncestors.forEach(el => {
          el.removeEventListener('scroll', this.scrollHandler)
        })
        this.scrollableAncestors = []
      }
      window.removeEventListener('scroll', this.scrollHandler)

      if (this.tomselect.wrapper) {
        this.tomselect.wrapper.classList.remove('dropdown-up')
      }
    })
  }

  updatePhoneNumberInput(input, localNumber) {
    const hidden = document.createElement('input')
    hidden.type = 'hidden'
    hidden.value = input.value
    hidden.name = input.name
    input.value = localNumber
    input.parentNode.appendChild(hidden)

    input.form.addEventListener('submit', () => {
      const phoneDigits = input.value.replace(/\D/g, '')
      hidden.value = phoneDigits ? `+${this.phoneNumberTarget.dataset.countryCode}${phoneDigits}` : ''
    })
  }

  disconnect() {
    if (this.popper) {
      this.popper.destroy()
      this.popper = null
    }
    if (this.tomselect) {
      this.tomselect.destroy()
      this.tomselect = null
    }
  }
}
